import { cn } from '@invent/wl-ui-kit-next';
import { AlertCircle } from 'lucide-react';

import type { IFinding, IProgress } from '../api/contracts';
import type { ISectionView } from './snapshot-view';

interface ISectionStepperProps {
  sections: ISectionView[];
  currentIndex: number;
  /** The findings shown (`visibleFindings`); a section is flagged by the ones in it. */
  findings: IFinding[];
  /** survey-api's count of required answers by section code (`state.progress.sections`); absent
   *  where nothing is counted — a version's preview. */
  progress?: IProgress['sections'];
  onSelect: (index: number) => void;
}

/**
 * Section navigation, down the left of the survey (the two-portals design, §3.3): numbered,
 * because the sections are a sequence. Every section is reachable at any time — answers are saved
 * continuously, so jumping back to fix something cannot lose work.
 *
 * "Needs attention" is the engine's verdict, not a local count: a section is flagged when a shown
 * hard finding is addressed to a usage inside it. Requiredness and visibility both move with the
 * rules, so anything computed here would drift from the gate that actually decides whether the
 * survey can be submitted. For the same reason there are no check marks: a clean section is not a
 * finished one, since the page holds a finding back until the respondent reaches it. Under the
 * title, "n of m required" is survey-api's count for the section (the survey review's G6); a
 * section it holds no required slot for says nothing.
 *
 * An `ol > li > button` per section, the current one `aria-current="step"`, each button named by
 * its number, its title, its count and "Needs attention" — the structure the suites walk.
 */
export function SectionStepper({
  sections,
  currentIndex,
  findings,
  progress,
  onSelect
}: ISectionStepperProps) {
  const blockedUsages = new Set(
    findings.filter((finding) => finding.severity === 'hard' && finding.usage).map((f) => f.usage!)
  );

  return (
    <ol className="grid gap-0.5">
      {sections.map((section, index) => {
        const isCurrent = index === currentIndex;
        const hasIssues = section.roots.some((node) => hasBlockedUsage(node, blockedUsages));
        const count = progress?.[section.section.code];

        return (
          <li key={section.section.code}>
            <button
              type="button"
              onClick={() => onSelect(index)}
              aria-current={isCurrent ? 'step' : undefined}
              className={cn(
                'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-[0.9375rem] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                isCurrent
                  ? // Sapphire + white: 6.5:1.
                    'bg-[var(--color-sapphire)] text-white'
                  : 'text-[var(--color-royal-blue)] hover:bg-[var(--color-slight-blue)]'
              )}
            >
              <span
                className={cn(
                  'flex size-[1.625rem] shrink-0 items-center justify-center rounded-full border text-[0.8125rem] tabular-nums',
                  hasIssues
                    ? // Deep Magenta + white: the AA-safe error pairing.
                      'border-transparent bg-[var(--color-deep-magenta)] text-white'
                    : isCurrent
                      ? 'border-transparent bg-white/20 text-white'
                      : 'border-border bg-white text-[var(--color-royal-blue)]'
                )}
              >
                {hasIssues ? <AlertCircle className="size-4" aria-hidden /> : index + 1}
              </span>
              <span className="min-w-0">
                <span className="block leading-snug">{section.section.title}</span>
                {count && (
                  <span
                    className={cn(
                      'block text-[0.8125rem] leading-snug tabular-nums',
                      isCurrent ? 'text-white/85' : 'text-muted-foreground'
                    )}
                    data-section-progress
                  >
                    {count.answered} of {count.required} required
                  </span>
                )}
                {hasIssues && (
                  <span
                    className={cn(
                      'block text-[0.8125rem] leading-snug',
                      isCurrent ? 'text-white/85' : 'text-[var(--color-deep-magenta)]'
                    )}
                  >
                    Needs attention
                  </span>
                )}
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function hasBlockedUsage(
  node: ISectionView['roots'][number],
  blocked: Set<string>
): boolean {
  if (blocked.has(node.usage.code)) return true;
  return (
    node.children.some((child) => hasBlockedUsage(child, blocked)) ||
    node.members.some((member) => hasBlockedUsage(member, blocked))
  );
}
